/* eslint-disable react/no-unescaped-entities */
/**
 * Learning Tutorial — deep-dive walkthrough of the full Freeze Dry Academy
 * classroom. Covers the catalog, tiers, taking a course, quizzes,
 * certificates, assignments, roadmap, streaks, AI Teacher, streaks,
 * anti-cheat, bookmarks, discussions, live events, offline access, and
 * troubleshooting.
 */
import React from 'react';
import ToolSelector from '../../components/ToolSelector';
import TutorialLayout, { Callout, StepList, TryItButton } from '../../components/TutorialLayout';

const sections = [
  {
    id: 'overview',
    title: 'What is the Learning section?',
    body: (
      <>
        <p>
          The <strong>Learning</strong> section is your operator-grade classroom for
          freeze drying — built by working operators for working operators. Every
          course is battle-tested on real home units, commercial cabinets, and industrial systems.
          No marketing fluff, no filler.
        </p>
        <p>Everything hangs off a simple hierarchy:</p>
        <ul>
          <li>
            <strong>Track</strong> — a career path (Hobbyist, Side Hustle, Full-Time Operator, Master Practitioner).
          </li>
          <li>
            <strong>Course</strong> — a self-contained subject inside a track (e.g. "Introduction to Freeze Drying").
          </li>
          <li>
            <strong>Module</strong> — a chapter inside the course, usually 30–90 minutes of material.
          </li>
          <li>
            <strong>Lesson</strong> — the actual video, text, exercise, or assignment inside a module.
          </li>
          <li>
            <strong>Checkpoint quiz</strong> — module gate; passing marks the module complete.
          </li>
          <li>
            <strong>Certificate</strong> — issued when every module quiz in a course is passed.
          </li>
        </ul>
        <Callout kind="tip">
          The left sub-navigation (Board / Courses / Certificates / Streaks / AI
          Teacher / Roadmap / Events) is sticky as you scroll — it's the fastest
          way to move around the classroom.
        </Callout>
        <TryItButton to="/learning" label="Open the Learning catalog" />
      </>
    ),
  },
  {
    id: 'tracks',
    title: 'Learning tracks & the roadmap',
    body: (
      <>
        <p>
          Tracks bundle courses in the order most operators actually take them:
        </p>
        <ul>
          <li><strong>Hobbyist</strong> — freeze drying for personal food storage. 4–6 courses.</li>
          <li><strong>Side Hustle</strong> — Etsy/local-farmers-market operators. Adds packaging, pricing, labels.</li>
          <li><strong>Full-Time Operator</strong> — cottage-food + small commercial. Adds compliance, wholesale, staffing.</li>
          <li><strong>Master Practitioner</strong> — the capstone. Emerging techniques, industrial equipment, teaching.</li>
        </ul>
        <p>
          Your <strong>Roadmap</strong> (Dashboard → right column) is a live visual of where
          you are on the track you selected during onboarding. The bar fills as you complete
          modules; the next milestone always has a "Start next module" button that jumps you
          straight in.
        </p>
        <Callout kind="info">
          You can be on multiple tracks at once. Switch the visible track from the sub-nav
          filter — the AI Teacher planner will re-order suggestions to match.
        </Callout>
      </>
    ),
  },
  {
    id: 'tiers',
    title: 'Membership tiers & what unlocks',
    body: (
      <>
        <p>
          The catalog is tier-filtered so you always see what you can actually take with
          your current membership:
        </p>
        <ul>
          <li><strong>Free</strong> — fundamentals, safety, and 4 getting-started courses.</li>
          <li><strong>Pro</strong> — full library, quizzes, certificates, AI Teacher planner, threaded discussion, bookmarks, streaks.</li>
          <li><strong>Business</strong> — everything in Pro plus cottage-food compliance, pricing playbooks, wholesale channel guides, assignments.</li>
          <li><strong>Enterprise</strong> — team seats, private cohorts, custom curriculum, live office hours.</li>
        </ul>
        <Callout kind="info">
          Courses above your tier show a lock icon. Click the lock for a preview lesson,
          the full syllabus, and an upgrade CTA. No paywall traps — you always see what
          you'd get before deciding.
        </Callout>
      </>
    ),
  },
  {
    id: 'catalog-filters',
    title: 'Filtering & searching the catalog',
    body: (
      <>
        <p>The catalog has four filter surfaces stacked on top of each other:</p>
        <ol>
          <li><strong>Search</strong> — full-text over titles, transcripts, and tags. Try "vacuum sensor" or "labeling".</li>
          <li><strong>Tier</strong> — Free / Pro / Business / Enterprise. Defaults to your current tier and above.</li>
          <li><strong>Status</strong> — Active (currently running cohort) vs Archived (evergreen self-serve).</li>
          <li><strong>Track</strong> — Hobbyist / Side Hustle / Full-Time / Master.</li>
        </ol>
        <p>
          Filters are URL-persisted, so a filtered view is shareable via link — great for
          bookmarking your favorite subset or sending "here's what I recommend" to a mentee.
        </p>
      </>
    ),
  },
  {
    id: 'taking-a-course',
    title: 'Taking your first course, step by step',
    body: (
      <>
        <StepList
          steps={[
            'From /learning, pick a course. Cards show tier, track, module count, and estimated hours.',
            'Read the course intro (right side) and click Start module. Each module opens with an overview and estimated time.',
            'Inside the LessonViewer: watch the video (or read the article), take notes in the built-in notepad, and mark the lesson complete when done.',
            'Some lessons have inline exercises (recipes to try, questions to answer). These auto-save every 20 seconds.',
            'Finish every lesson in the module to unlock the module checkpoint quiz.',
            'Pass the quiz (80% by default) — the module gets a green check.',
            'When every module quiz is passed, a 3D "Course Complete" video plays and a certificate is issued to your account.',
          ]}
        />
        <Callout kind="tip">
          Video playback remembers your position for 30 days. Come back tomorrow and hit
          resume — no need to scrub back.
        </Callout>
      </>
    ),
  },
  {
    id: 'lesson-viewer',
    title: 'Inside the Lesson Viewer',
    body: (
      <>
        <p>Every lesson has four tabs at the top:</p>
        <ul>
          <li><strong>Watch/Read</strong> — the primary content, streamed from our CDN. HLS adaptive bitrate.</li>
          <li><strong>Resources</strong> — PDF notes, cycle sheets, recipe cards, and vendor links.</li>
          <li>
            <strong>Discussion</strong> — threaded conversation attached to this exact lesson. Replies notify the
            instructor and mirror into the Community forum's Learning-linked category.
          </li>
          <li><strong>Notes</strong> — your private notepad. Never leaves your account. Markdown-capable.</li>
        </ul>
        <p>
          The right rail shows the module's other lessons with checkmarks against completed
          ones. A vertical progress bar on the very edge fills as you scrub through the video.
        </p>
      </>
    ),
  },
  {
    id: 'quizzes',
    title: 'Quizzes & how the anti-cheat works',
    body: (
      <>
        <p>
          Each module ends with a checkpoint quiz — typically 8–15 questions, multiple-choice
          plus a few short-answer. Pass mark defaults to 80%. You get unlimited attempts, but
          each attempt shuffles questions and answer order to prevent memorization.
        </p>
        <p>Anti-cheat is on by default:</p>
        <ul>
          <li>Leaving the tab or window for more than ~5 seconds voids the attempt.</li>
          <li>Right-click, copy, and text selection are disabled during a live quiz.</li>
          <li>The timer starts when the first question loads and pauses when the tab is refocused.</li>
        </ul>
        <Callout kind="warning">
          Don't start a quiz "just to see the questions" — voided attempts count against
          your daily attempt cap (5/day). Come back with a clear 15 minutes when you're ready.
        </Callout>
      </>
    ),
  },
  {
    id: 'certificates',
    title: 'Certificates & badges',
    body: (
      <>
        <p>
          Passing every module quiz in a course automatically issues a <strong>procedural
          certificate</strong> — a verifiable, printable credential with a unique ID that anyone
          can look up at <code>/verify/&lt;certificate-id&gt;</code>. Certificates carry:
        </p>
        <ul>
          <li>Your full name (from your profile at time of issue).</li>
          <li>The exact course title, module count, and issued-at timestamp.</li>
          <li>A one-click LinkedIn Share button that pre-fills the credential card.</li>
          <li>An OG-tagged share card so links in Discord/Slack render as rich previews.</li>
        </ul>
        <p>Badges are separate from certificates — they light up for milestones like:</p>
        <ul>
          <li>First batch logged from a lesson</li>
          <li>7-day and 30-day learning streaks</li>
          <li>Completing every Business tier course</li>
          <li>Being the first to ask a question in a new lesson's discussion</li>
        </ul>
        <TryItButton to="/my-certificates" label="View my certificates" />
      </>
    ),
  },
  {
    id: 'assignments',
    title: 'Assignments (Business & Enterprise)',
    body: (
      <>
        <p>
          Business and Enterprise courses include <strong>assignments</strong> — real
          deliverables that get graded. Examples:
        </p>
        <ul>
          <li>Upload your cottage-food label as a PDF; graders check compliance with your state's requirements.</li>
          <li>Record a 30-second batch log video and upload it.</li>
          <li>Submit a spreadsheet showing your target cost-per-ounce for a product line.</li>
        </ul>
        <p>
          Uploads go through secure object storage (large files supported, chunked). Grading
          returns one of: <strong>Distinction</strong>, <strong>Pass</strong>, or
          <strong> Needs work</strong> with written feedback. Distinctions unlock a special
          badge that appears on your community profile.
        </p>
      </>
    ),
  },
  {
    id: 'discussion',
    title: 'Discussion inside a lesson',
    body: (
      <>
        <p>
          Every lesson has a <strong>threaded discussion</strong> below the video. Replies notify
          the instructor by default. Two useful conventions:
        </p>
        <ul>
          <li>
            <strong>Bring a photo.</strong> "Why is my batch weird" answers land 10× faster with an
            image of the tray.
          </li>
          <li>
            <strong>Post the exact time-code.</strong> Reference "at 4:32 you mention…" — makes it
            easy for the instructor to jump straight to context.
          </li>
        </ul>
        <p>
          Lesson discussions are automatically mirrored into the Community forum's
          Learning-linked category so answers benefit everyone, not just people watching
          that specific lesson.
        </p>
      </>
    ),
  },
  {
    id: 'bookmarks-notes',
    title: 'Bookmarks & the notepad',
    body: (
      <>
        <p>Two lightweight productivity tools baked into the classroom:</p>
        <ul>
          <li>
            <strong>Bookmarks</strong> — ribbon icon on any course card, lesson, or resource.
            Bookmarked items get their own tab in the sub-nav so you can jump back later.
          </li>
          <li>
            <strong>Notes</strong> — inside every lesson, the Notes tab is a Markdown-capable
            private notepad. Notes are searchable across your account from
            <em> Dashboard → Notes</em>.
          </li>
        </ul>
        <Callout kind="tip">
          Copy your batch results into the lesson notepad instead of a separate spreadsheet.
          The AI Teacher planner reads your notes when recommending next steps.
        </Callout>
      </>
    ),
  },
  {
    id: 'progress-streaks',
    title: 'Progress tracking & streaks',
    body: (
      <>
        <p>
          The Dashboard has two learning widgets pulling live data:
        </p>
        <ul>
          <li>
            <strong>Learning roadmap</strong> — visual path from your current tier to Master
            Practitioner. Shows % complete per stage.
          </li>
          <li>
            <strong>Achievements strip</strong> — every certificate and badge you've earned,
            plus a "next up" highlight for what you're closest to.
          </li>
        </ul>
        <p>
          A daily learning <strong>streak</strong> starts once you complete a lesson two days in
          a row. Miss a day and the counter resets — but Pro+ tiers get <strong>grace days</strong>
          (2/month) so a planned break doesn't cost the streak.
        </p>
        <TryItButton to="/dashboard" label="See my roadmap" />
      </>
    ),
  },
  {
    id: 'ai-teacher',
    title: 'The AI Teacher planner (Pro+)',
    body: (
      <>
        <p>
          <strong>AI Teacher</strong> reads your Skills Radar, batch history, lesson notes, and
          stated goal, then plans a bespoke study path — day-by-day lessons with time budgets.
          Regenerate it anytime your goal changes.
        </p>
        <StepList
          steps={[
            'Open /learning and click Prof. FreedrIQ in the sub-nav (or on the CTA card).',
            'Answer three prompts: goal, weekly hours, comfort with tech.',
            'The AI returns a 14-day plan you can review, edit, and pin to Dashboard.',
            'Each morning "Today\'s lesson" surfaces on your Dashboard workbench.',
            'Complete lessons ahead of schedule to bank days — the AI reschedules automatically.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'live-events',
    title: 'Live events & office hours',
    body: (
      <>
        <p>
          On top of self-serve courses, we run scheduled <strong>live events</strong> — office
          hours, guest AMAs, batch teardowns. They live in the Events tab of the sub-nav and
          also show up on the Community forum's Events category.
        </p>
        <p>Types of events:</p>
        <ul>
          <li><strong>Office hours</strong> — 60 minutes with an instructor. Bring your batch photos.</li>
          <li><strong>AMAs</strong> — 60–90 minutes with a guest operator (industrial units, wholesale accounts, etc.).</li>
          <li><strong>Live teardowns</strong> — instructor takes a real failed batch and walks through the debug.</li>
          <li><strong>Cohort kickoffs</strong> — for Business tier courses that run in cohorts.</li>
        </ul>
        <Callout kind="info">
          RSVP counts as event enrollment. Missed events automatically appear on your Roadmap
          as "watch the recording" tasks — recordings ship within 24 hours.
        </Callout>
      </>
    ),
  },
  {
    id: 'offline-mobile',
    title: 'Mobile & offline access',
    body: (
      <>
        <p>
          Every page is mobile-first — take a lesson on the phone while your machine is
          finishing a cycle. Two mobile-specific tricks:
        </p>
        <ul>
          <li>
            <strong>Save for offline</strong> (Pro+) — long-press any lesson card to cache the
            video + resources. Kicks in even without service.
          </li>
          <li>
            <strong>Batch log widget</strong> — a floating shortcut appears on lesson pages
            when you're logged in on mobile. Tap to log a batch without leaving the lesson.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting the classroom',
    body: (
      <>
        <h3>The video won't play</h3>
        <p>Check the network indicator (bottom-right of the LessonViewer). Amber means we're
          throttling to a lower bitrate. If it stays red for 30 seconds, hit refresh — the
          player auto-recovers 95% of the time.</p>
        <h3>My progress isn't saving</h3>
        <p>Progress is written every 20 seconds. If you switch devices mid-lesson,
          wait 30 seconds after finishing before jumping to the other device.</p>
        <h3>A course card is greyed out</h3>
        <p>Two possible reasons: (a) it's above your tier, click for upgrade preview, or
          (b) it has a prerequisite course you haven't finished. Prerequisites are listed
          on the course intro page.</p>
        <h3>I want a refund</h3>
        <p>Every paid course has a 14-day money-back window from first watch. Cancel via
          UserPortal → Billing. Certificates issued in that window are revoked.</p>
        <Callout kind="success">
          Still stuck? Message any admin from their profile card — DMs are 1:1 and usually
          answered within 24 hours. Or post in the Community's <em>Feedback & Bugs</em> category.
        </Callout>
      </>
    ),
  },
  {
    id: 'pro-tips',
    title: 'Pro tips from the team',
    body: (
      <>
        <ul>
          <li>
            <strong>Bookmark before you start.</strong> Bookmarking a course adds it to your
            Roadmap even before you begin — the AI Teacher will factor it in.
          </li>
          <li>
            <strong>Take notes in the lesson.</strong> The AI Teacher reads them. Fresh, contextual
            notes make the next-step suggestions dramatically better.
          </li>
          <li>
            <strong>Answer one question per week</strong> in a lesson's discussion. It cements
            what you learned better than any solo re-watch — and you earn community rep.
          </li>
          <li>
            <strong>Chain lessons under 20 minutes.</strong> Streaks are about consistency,
            not intensity. A 15-minute lesson tomorrow beats a 90-minute lesson next week.
          </li>
        </ul>
      </>
    ),
  },
];

export default function TutorialLearning() {
  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <TutorialLayout
        eyebrow="Tutorial"
        title="Learning section — deep dive"
        subtitle="Everything you need to make the most of the Freeze Dry Academy classroom — from your first free lesson to your Master Practitioner certificate. 17 sections, ~15 minute read."
        sections={sections}
        backTo="/learning"
        backLabel="Back to Learning"
      />
    </div>
  );
}
